import { Component, createRef, h, RefObject } from "preact";
import classNames from "classnames";

import { toRenewalTimeUnit } from "../../utils/misc";
import { getExpirationDate } from "../../utils/expiration-date";
import { ShoppingCartIcon } from "./icons/ShoppingCartIcon";
import { CloseIcon } from "./icons/CloseIcon";
import { formatCartQuantity } from "../../utils/shopping-cart";
import { getPersistedState, savePersistedState } from "../../app/state";
import { Placement } from "../../app/config";

import "./ShoppingCartButton.scss";

const POPOVER_CLASS_NAME = "usc-shopping-cart-popover";

interface ShoppingCartProps {
    lang: string;
    cartItemsCount: number;
    isHidden: boolean;
    storageKey: string;
    popoverRenewalTimeAmount: number;
    popoverRenewalTimeUnit: string;
    openShoppingCartFn: () => void;
    isHideButton: boolean;
    placement?: Placement;
    tooltipEnabled: boolean;
}

interface ShoppingCartState {
    expirationDate: number;
    isPopoverTouched: boolean;
}

export default class ShoppingCartButton extends Component<
    ShoppingCartProps,
    ShoppingCartState
> {
    private pluralRules?: Intl.PluralRules;

    buttonRef: RefObject<HTMLButtonElement>;

    constructor(props: ShoppingCartProps) {
        super(props);

        const storedState = getPersistedState(props.storageKey);

        this.state = {
            expirationDate: storedState.expirationDate ?? 0,
            isPopoverTouched: storedState.isPopoverTouched ?? false
        };

        // Intl.PluralRules is supported on Safari since v13
        this.pluralRules =
            typeof Intl.PluralRules !== "function"
                ? undefined
                : new Intl.PluralRules("en-US");

        this.buttonRef = createRef<HTMLButtonElement>();
    }

    componentDidMount() {
        const { expirationDate } = this.state;
        const { popoverRenewalTimeUnit, popoverRenewalTimeAmount } = this.props;
        const castedPopoverRenewalTimeUnit = toRenewalTimeUnit(
            popoverRenewalTimeUnit
        );
        if (Date.now() > expirationDate) {
            const date = getExpirationDate(
                castedPopoverRenewalTimeUnit,
                popoverRenewalTimeAmount
            );
            const isPopoverTouched = false;

            this.saveState(date, isPopoverTouched);
        }

        document.addEventListener("click", this.handleDocumentClick);
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.handleDocumentClick);
    }

    handleDocumentClick = (e: MouseEvent) => {
        const { isPopoverTouched } = this.state;
        const { isHidden } = this.props;
        const target = e.target as HTMLElement;
        const className = String(target.className);

        if (
            !isPopoverTouched &&
            !isHidden &&
            !className.includes(POPOVER_CLASS_NAME)
        ) {
            this.handlePopoverClose();

            document.removeEventListener("click", this.handleDocumentClick);
        }
    };

    handlePopoverClose = () => {
        this.markAsTouched();
    };

    private markAsTouched = () => {
        const { popoverRenewalTimeUnit, popoverRenewalTimeAmount } = this.props;
        const castedPopoverRenewalTimeUnit = toRenewalTimeUnit(
            popoverRenewalTimeUnit
        );
        const expirationDate = getExpirationDate(
            castedPopoverRenewalTimeUnit,
            popoverRenewalTimeAmount
        );
        const isPopoverTouched = true;

        this.saveState(expirationDate, isPopoverTouched);
    };

    private saveState = (expirationDate: number, isPopoverTouched: boolean) => {
        const { storageKey } = this.props;
        this.setState({
            expirationDate,
            isPopoverTouched
        });

        const persistedState = getPersistedState(storageKey);

        persistedState.expirationDate = expirationDate;
        persistedState.isPopoverTouched = isPopoverTouched;

        savePersistedState(storageKey, persistedState);
    };

    private handleShoppingBtnClick = (e: Event) => {
        const { isPopoverTouched } = this.state;
        const { openShoppingCartFn } = this.props;
        if (!isPopoverTouched) {
            this.markAsTouched();
        }
        e.preventDefault();
        openShoppingCartFn();
    };

    private pluralize = (count: string) => {
        const grammaticalNumber = this.pluralRules?.select(Number(count));
        if (grammaticalNumber === "one") return `${count} product`;
        return `${count} products`;
    };

    render() {
        const {
            isHidden: isMovedOutsideScreen,
            lang,
            cartItemsCount,
            isHideButton,
            placement,
            tooltipEnabled
        } = this.props;
        const { isPopoverTouched } = this.state;

        const isButtonHidden =
            window.uscWidget?.config?.hideCartButton || isHideButton;

        const itemsCount = formatCartQuantity(cartItemsCount);

        return (
            <div
                className={classNames("usc-shopping-cart-btn-container", {
                    "usc-shopping-cart-btn-container--top": placement === "top",
                    "usc-shopping-cart-btn-container--center":
                        placement === "center",
                    "is-hidden": isMovedOutsideScreen || isHideButton,
                    "hide-button": window.uscWidget?.config?.hideCartButton
                })}
                aria-hidden={isButtonHidden ? "true" : "false"}
            >
                {!isPopoverTouched &&
                    !isMovedOutsideScreen &&
                    tooltipEnabled && (
                        <div className={POPOVER_CLASS_NAME} role="tooltip">
                            <div className="usc-shopping-cart-tooltip-arrow" />
                            <div className="usc-shopping-cart-popover-body">
                                <div className="usc-shopping-cart-popover-title">
                                    {lang.startsWith("es")
                                        ? "Comprar productos destacados"
                                        : "Shop featured products"}
                                </div>
                                <button
                                    type="button"
                                    className="usc-shopping-cart-popover-close-btn"
                                    aria-label="Close"
                                    onClick={this.handlePopoverClose}
                                >
                                    <CloseIcon />
                                </button>
                            </div>
                        </div>
                    )}
                <button
                    ref={this.buttonRef}
                    type="button"
                    className="usc-shopping-cart"
                    onClick={this.handleShoppingBtnClick}
                    aria-label="Shop and open your cart"
                    aria-describedby="usc-shopping-cart-button-description"
                    data-testid="usc-shopping-cart-button"
                >
                    <ShoppingCartIcon cartItemsCount={itemsCount} />
                </button>
                <p
                    id="usc-shopping-cart-button-description"
                    style={{ display: "none" }}
                >
                    {`Your cart contains ${this.pluralize(itemsCount)}`}
                </p>
            </div>
        );
    }
}
